import React from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { lifestyleFor } from "@/data/lifestyle";

/**
 * HomePreview — three alternate home-hero layouts stacked with the current
 * one at top for comparison. Live at /home-preview.
 */

const SectionHeader = ({ kicker, title, note }) => (
  <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-10 hairline-t">
    <div className="grid md:grid-cols-12 gap-6 items-end">
      <div className="md:col-span-8">
        <div className="nav-item mb-2" style={{ color: "var(--accent)" }}>{kicker}</div>
        <h2 className="serif text-3xl md:text-5xl leading-[1.05]">{title}</h2>
      </div>
      <p className="md:col-span-4 text-sm text-ink/70 leading-relaxed">{note}</p>
    </div>
  </div>
);

const HERO_IMG = "/api/uploads/butterfly-bliss-pink/lifestyle-full.webp";
const TWINNING_IMG = "/api/uploads/twinning/busy-bee/pair-hero.webp";
const WOMEN_IMG = "/api/uploads/_lifestyle/women/tea-table.webp";
const CRAFT_IMG = "/api/uploads/craft/embroidery-hoop-hands.webp";

/* ============ CURRENT · v0 for comparison ============ */
const VariantCurrent = () => (
  <section className="relative w-full overflow-hidden" style={{ minHeight: "600px" }}>
    <img src={imgUrl(HERO_IMG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(246,241,233,0.55) 0%, rgba(246,241,233,0.15) 60%, transparent 100%)" }} />
    <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-20 md:pb-28">
      <div className="max-w-[720px]">
        <div className="nav-item mb-4">Chapter One · The Colonnade</div>
        <h1 className="serif text-5xl md:text-8xl leading-[1.02]">
          Small clothes, made <em className="italic">slowly.</em>
        </h1>
        <div className="mt-10 nav-item text-ink/60">On the plinth today</div>
        <div className="serif text-3xl md:text-4xl mt-1">Butterfly Bliss Pink</div>
        <div className="mt-6 flex flex-wrap gap-4">
          <Link to="/product/butterfly-bliss-pink" className="btn-solid">See the piece</Link>
          <Link to="/shop" className="btn-ink">Enter the shop →</Link>
        </div>
      </div>
    </div>
  </section>
);

/* ============ VARIANT A · TWIN SPLIT ============
   Symmetrical split screen — a girl's piece on the left,
   a twinning pair on the right, hairline centre divider
   with the brand mark. "A family label." */
const VariantTwinSplit = () => (
  <section className="relative w-full grid md:grid-cols-2" style={{ background: "var(--ground)" }}>
    <div className="relative aspect-[3/4] md:aspect-auto md:min-h-[720px] overflow-hidden">
      <img src={imgUrl(HERO_IMG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(28,26,25,0.5) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 p-8 md:p-14 text-ground">
        <div className="text-[10px] tracking-[0.28em] uppercase opacity-80 mb-3">For the small ones</div>
        <div className="serif text-3xl md:text-5xl leading-tight">Butterfly Bliss Pink</div>
        <Link to="/shop" className="mt-6 inline-block nav-item hover:opacity-70">Shop children →</Link>
      </div>
    </div>
    <div className="relative aspect-[3/4] md:aspect-auto md:min-h-[720px] overflow-hidden">
      <img src={imgUrl(TWINNING_IMG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(28,26,25,0.5) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 p-8 md:p-14 text-ground">
        <div className="text-[10px] tracking-[0.28em] uppercase opacity-80 mb-3">For the whole family</div>
        <div className="serif text-3xl md:text-5xl leading-tight">Twinning · Busy Bee</div>
        <Link to="/twinning" className="mt-6 inline-block nav-item hover:opacity-70">Shop twinning →</Link>
      </div>
    </div>
    {/* centre badge */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:block">
      <div className="w-32 h-32 rounded-full flex items-center justify-center text-center" style={{ background: "var(--ground)", border: "1px solid var(--ink)" }}>
        <div>
          <div className="serif italic text-lg">MELAVI</div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-ink/60 mt-1">A family label</div>
        </div>
      </div>
    </div>
  </section>
);

/* ============ VARIANT B · VERTICAL CINEMA REEL ============
   Full-viewport hero, one massive serif line, kicker on top,
   a subtle grain + slow ken-burns scale animation. Feels like
   a film title card. */
const VariantCinemaReel = () => (
  <section className="relative w-full overflow-hidden" style={{ minHeight: "88vh", background: "var(--ink)" }}>
    <img src={imgUrl(CRAFT_IMG)} alt="" className="absolute inset-0 w-full h-full object-cover animate-[cinema-pan_18s_ease-in-out_infinite_alternate]" style={{ opacity: 0.75 }} />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,26,25,0.35) 0%, rgba(28,26,25,0.2) 40%, rgba(28,26,25,0.85) 100%)" }} />
    <div className="grain absolute inset-0 opacity-40" />
    <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pt-24 md:pt-40 pb-16 md:pb-24 text-ground">
      <div className="text-[10px] tracking-[0.32em] uppercase mb-8" style={{ color: "rgba(246,241,233,0.7)" }}>
        MELAVI · Issue N&ordm; 01 · Spring 2026
      </div>
      <h1 className="serif leading-[0.95] text-[52px] md:text-[124px]" style={{ color: "var(--ground)" }}>
        Small clothes.
      </h1>
      <h1 className="serif italic leading-[0.95] text-[52px] md:text-[124px] mt-1" style={{ color: "var(--ground)" }}>
        Slow love.
      </h1>
      <p className="mt-10 max-w-[52ch] leading-relaxed text-ground/80">
        A digital flagship for the hand-dyed, hand-embroidered pieces MELAVI has been quietly making from a rooftop in Bengaluru since 2024.
      </p>
      <div className="mt-12 flex flex-wrap items-center gap-6">
        <Link to="/shop" className="btn-solid" style={{ background: "var(--ground)", color: "var(--ink)", borderColor: "var(--ground)" }}>Enter the showroom</Link>
        <Link to="/the-craft" className="nav-item" style={{ color: "var(--stone)" }}>Read the craft →</Link>
      </div>
    </div>
  </section>
);

/* ============ VARIANT C · ALBUM SPREAD ============
   Editorial magazine double-page: big lead piece on the left,
   a "In this issue" contents list of 4 items on the right with
   hairline separators and page-number style numerals. */
const VariantAlbumSpread = ({ items }) => {
  const rows = [
    { n: "01", label: "The Colonnade", sub: "Butterfly Bliss Pink · girls", href: "/product/butterfly-bliss-pink" },
    { n: "02", label: "The Rooftop", sub: "Busy Bee · family twinning", href: "/twinning" },
    { n: "03", label: "The Garden", sub: "Grown-ups · magenta twilight", href: "/motif/butterfly" },
    { n: "04", label: "The Atelier", sub: "The Craft · six rooms", href: "/the-craft" },
  ];
  return (
    <section className="relative w-full grid md:grid-cols-12 gap-0" style={{ background: "#F8F2E4", minHeight: "780px" }}>
      <div className="md:col-span-7 relative min-h-[540px] md:min-h-[780px] overflow-hidden">
        <img src={imgUrl(HERO_IMG)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute bottom-0 left-0 p-6 md:p-10 text-[10px] tracking-[0.28em] uppercase" style={{ color: "rgba(246,241,233,0.85)" }}>
          Cover · Butterfly Bliss Pink · Bengaluru
        </div>
      </div>
      <div className="md:col-span-5 p-8 md:p-14 flex flex-col justify-between">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-ink/60 mb-3">MELAVI · Issue N&ordm; 01</div>
          <h1 className="serif text-4xl md:text-6xl leading-[1.02]">
            Small clothes, <br />made <em className="italic">slowly.</em>
          </h1>
          <p className="mt-6 text-ink/70 leading-relaxed max-w-[42ch]">
            A digital flagship, opened this spring. Twenty-five pieces on the table, hand-dyed and hand-embroidered.
          </p>
        </div>
        <div className="mt-10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-ink/60 mb-4">In this issue</div>
          <ul>
            {rows.map((r, i) => (
              <li key={i} className={`flex items-baseline justify-between gap-6 py-4 ${i > 0 ? "hairline-t" : ""}`}>
                <div className="flex items-baseline gap-5">
                  <span className="serif italic text-2xl" style={{ color: "var(--accent)" }}>{r.n}</span>
                  <div>
                    <Link to={r.href} className="serif text-xl md:text-2xl hover:italic transition-all">{r.label}</Link>
                    <div className="nav-item mt-1 text-ink/50">{r.sub}</div>
                  </div>
                </div>
                <span className="text-ink/40 text-lg">→</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const HomePreview = () => (
  <div data-testid="home-preview-page">
    <VariantCurrent />
    <SectionHeader
      kicker="A · Twin Split"
      title="A family label, told in two halves."
      note="Left half: the children's piece. Right half: the twinning pair. A small MELAVI badge holds the seam. Signals family-label positioning from the first frame."
    />
    <VariantTwinSplit />

    <SectionHeader
      kicker="B · Cinema Reel"
      title="One line. One frame. Slow burn."
      note="Full-viewport dark hero with a slow Ken-Burns pan on an atelier photo. Big two-line serif with an italic second line. Feels like a magazine title card."
    />
    <VariantCinemaReel />

    <SectionHeader
      kicker="C · Album Spread"
      title="Issue Nº 01, laid out like a magazine."
      note="Lead product on the left as the cover; on the right, an 'In this issue' contents list with page numbers linking to each destination. Best if you want to signal that MELAVI is a serial, not a shop."
    />
    <VariantAlbumSpread />

    <div className="py-16 text-center hairline-t" style={{ background: "var(--ground)" }}>
      <div className="serif italic text-2xl">Which home hero should we lock in?</div>
      <div className="text-sm text-ink/60 mt-2">Tell me A, B, C — or keep Current.</div>
    </div>
  </div>
);

export default HomePreview;
